import React, { useState, useEffect, useRef } from 'react';
import { FaMapMarkerAlt, FaSearch } from 'react-icons/fa';

const AddressAutocomplete = ({ 
  name,
  label,
  placeholder,
  required = false,
  initialValue = '',
  onAddressSelect,
  className = '',
  labelClassName = '',
  inputClassName = '',
  origin = '',
  dropdownAbove = false
}) => {
  const [address, setAddress] = useState(initialValue);
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  
  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  // Actualizar address cuando initialValue cambie
  useEffect(() => {
    setAddress(initialValue);
  }, [initialValue]);

  // Inicializar servicios de Google Maps
  useEffect(() => {
    const initializeGoogleMaps = async () => {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        setError('Google Maps API key no configurada en .env');
        console.error('Falta REACT_APP_GOOGLE_MAPS_API_KEY en .env');
        return;
      }

      if (apiKey === 'YOUR_GOOGLE_MAPS_API_KEY_HERE') {
        setError('Debes reemplazar la API key por una real');
        console.error('API key no configurada correctamente');
        return;
      }

      if (!window.google) {
        // Cargar script de Google Maps manualmente
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&language=es&region=CL`;
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
          try {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
            placesService.current = new window.google.maps.places.PlacesService(
              document.createElement('div')
            );
            setIsInitialized(true);
            setError(null);
            //console.log('Google Maps API cargada correctamente');
          } catch (err) {
            setError('Error al inicializar Google Maps');
            console.error('Error al inicializar Google Maps:', err);
          }
        };
        
        script.onerror = () => {
          setError('Error al cargar Google Maps API. Verifica tu API key y que las APIs estén habilitadas');
          console.error('Error al cargar Google Maps API. Verifica:');
          console.error('1. API key válida');
          console.error('2. Places API habilitada en Google Cloud Console');
          console.error('3. Restricciones de dominio configuradas');
        };
        
        document.head.appendChild(script);
      } else {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        placesService.current = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );
        setIsInitialized(true);
      }
    };

    initializeGoogleMaps();
  }, []);

  // Buscar predicciones de direcciones
  const searchPredictions = (input) => {
    if (!input || !autocompleteService.current || input.length < 3 || !isInitialized) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    setIsLoading(true);

    // Usar AutocompletionRequest en lugar de getQueryPredictions
    const request = {
      input: input,
      componentRestrictions: { country: 'cl' },
      types: ['address'],
      language: 'es',
      region: 'CL'
    };

    autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
      setIsLoading(false);
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setPredictions(predictions);
        setShowPredictions(true);
      } else {
        setPredictions([]);
        setShowPredictions(false);
      }
    });
  };

  // Manejar cambio en el input
  const handleInputChange = (e) => {
    const value = e.target.value;
    setAddress(value);
    searchPredictions(value);
  };

  // Seleccionar una predicción
  const selectPrediction = (prediction) => {
    setAddress(prediction.description);
    setShowPredictions(false);
    
    // Obtener detalles del lugar para extraer información adicional
    if (placesService.current && onAddressSelect) {
      placesService.current.getDetails(
        { placeId: prediction.place_id },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            const addressComponents = place.address_components;
            
            // Extraer ciudad/comuna
            const cityComponent = addressComponents.find(
              component => component.types.includes('locality') || 
                          component.types.includes('administrative_area_level_2')
            );
            
            // Extraer región
            const regionComponent = addressComponents.find(
              component => component.types.includes('administrative_area_level_1')
            );

            const locationData = {
              address: place.formatted_address,
              city: cityComponent ? cityComponent.long_name : '',
              region: regionComponent ? regionComponent.long_name : '',
              coordinates: place.geometry ? {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              } : null
            };

            onAddressSelect(locationData);
          }
        }
      );
    }
  };

  // Cerrar predicciones al hacer clic fuera
  const handleBlur = () => {
    // Delay para permitir que el click en la predicción se registre
    setTimeout(() => {
      setShowPredictions(false);
    }, 200);
  };

  return (
    <div className={`relative ${className}`}>
      <label className={labelClassName}>
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <input
          type="text"
          name={name}
          value={address}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={inputClassName}
          autoComplete="off"
          disabled={!isInitialized}
        />
        
        {/* Icono de búsqueda o loading */}
        <div className="absolute top-3.5 right-4 pointer-events-none">
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          ) : (
            <FaSearch className="text-gray-400 text-sm" />
          )}
        </div>
      </div>
      
      {/* Predicciones de direcciones */}
      {showPredictions && predictions.length > 0 && (
        <div className={`absolute z-[9999] w-full bg-gray-700 border border-gray-600 rounded shadow-lg max-h-60 overflow-y-auto ${dropdownAbove ? 'bottom-full mb-1' : 'mt-1'}`}>
          {predictions.map((prediction) => (
            <div
              key={prediction.place_id}
              onClick={() => selectPrediction(prediction)}
              className="px-3 py-2 cursor-pointer hover:bg-gray-600 border-b border-gray-600 last:border-b-0 transition-colors"
            >
              <div className="flex items-center">
                <FaMapMarkerAlt className="text-blue-400 mr-2 flex-shrink-0" />
                <span className="text-sm text-gray-300">
                  {prediction.description}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Mensaje si no hay resultados */}
      {showPredictions && predictions.length === 0 && address.length > 3 && !isLoading && (
        <div className={`absolute z-[9999] w-full bg-gray-700 border border-gray-600 rounded shadow-lg p-3 ${dropdownAbove ? 'bottom-full mb-1' : 'mt-1'}`}>
          <div className="flex items-center text-gray-400">
            <FaSearch className="mr-2" />
            <span className="text-sm">No se encontraron direcciones</span>
          </div>
        </div>
      )}
      
      {/* Mensaje de error */}
      {error && (
        <div className="mt-2 p-2 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;